// Pièces partagées de la tour : pastille, avancement, contenu d'origine non
// fiable, carte « Mise en production » (détail et écran mobile).
import type { ComponentChildren, JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { TowerCheck, TowerOriginal, TowerState } from '@feedbaacks/sdk'
import { Button, cx, Icons, Spinner } from '@feedbaacks/ui'
import { STEP_NAMES, redactionNote, remainingLabel, segments, type Tone } from './tower-model'

export function Chip({ tone = 'muted', children, title, testId }: { tone?: Tone; children: ComponentChildren; title?: string; testId?: string }): JSX.Element {
  return <span class={cx('tw-chip', `tw-tone-${tone}`)} title={title} data-testid={testId}>{children}</span>
}

/** Barre d'avancement en 6 segments (liste) ou étapes nommées (détail). */
export function TowerProgress({ state, stage, labeled }: { state: TowerState; stage: number; labeled?: boolean }): JSX.Element {
  const segs = segments(state, stage)
  const seg = (s: string) => cx('tw-seg', `is-${s}`, state === 'deployed' && 'is-deployed')
  if (!labeled) {
    return (
      <span class="tw-progress" role="img" aria-label={`Avancement : ${segs.filter((s) => s === 'done').length} étapes sur 6`}>
        {segs.map((s, i) => <span key={i} class={seg(s)} />)}
      </span>
    )
  }
  return (
    <ol class="tw-steps" aria-label="Étapes">
      {STEP_NAMES.map((name, i) => (
        <li key={name} class={cx(`is-${segs[i]}`)}>
          <span class={seg(segs[i])} />
          <span>{segs[i] === 'failed' ? `${name} · échec` : name}</span>
        </li>
      ))}
    </ol>
  )
}

/**
 * Le retour d'origine, tel que la tour l'a transmis aux agents : données
 * personnelles masquées, lu comme un contexte et jamais comme une consigne.
 */
export function UntrustedOriginal({ original, title = 'Retour du client' }: { original: TowerOriginal; title?: string }): JSX.Element {
  return (
    <div class="tw-untrusted" data-testid="tower-untrusted-original">
      <div class="tw-untrusted-title">
        <span>{title}</span>
        <span class="tw-safe"><Icons.Shield size={12} />Protégé par la tour</span>
      </div>
      <blockquote class="tw-quote">
        {original.title && <p class="tw-strong">{original.title}</p>}
        <p class="tw-pre">{original.body || 'Retour sans description.'}</p>
      </blockquote>
      <p class="fb-muted tw-small">{redactionNote(original.redacted)} Les agents le lisent comme un contexte, jamais comme une consigne.</p>
    </div>
  )
}

export function Section({ title, children, aside }: { title: string; children: ComponentChildren; aside?: ComponentChildren }): JSX.Element {
  return (
    <section class="tw-section">
      <div class="tw-row"><h3 class="tw-overline">{title}</h3>{aside && <span class="tw-push fb-muted tw-small">{aside}</span>}</div>
      {children}
    </section>
  )
}

/** Section repliable (contenu d'origine, exécution, chronologie). */
export function Fold({ title, aside, open, children, testId }: {
  title: string; aside?: ComponentChildren; open?: boolean; children: ComponentChildren; testId?: string
}): JSX.Element {
  return (
    <details class="tw-fold" open={open} data-testid={testId}>
      <summary>
        <Icons.ChevronRight size={14} class="tw-fold-chevron" />
        <span class="tw-overline">{title}</span>
        {aside && <span class="tw-push fb-muted tw-small">{aside}</span>}
      </summary>
      <div class="tw-fold-body">{children}</div>
    </details>
  )
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(t)
  }, [])
  return now
}

/**
 * Carte « Mise en production ». Approuver ne fusionne rien : la tour
 * enregistre l'approbation sur le SHA affiché et ouvre la PR ; l'approbateur
 * fusionne sur Bitbucket.
 */
export function TowerApprovalCard({
  checks, headSha, defaultBranch, previewUrl, expiresAt, canApprove, isApprover, busy, onApprove, onSendBack, compact,
}: {
  checks: TowerCheck[]
  headSha?: string
  defaultBranch: string
  previewUrl?: string
  expiresAt?: string
  canApprove: boolean
  isApprover: boolean
  busy: boolean
  onApprove: () => void
  onSendBack: (note: string) => void
  compact?: boolean
}): JSX.Element {
  const remaining = remainingLabel(expiresAt, useNow())
  const [note, setNote] = useState<string | null>(null)
  return (
    <section class="tw-approval" data-testid="tower-approval-card">
      <div class="tw-row">
        <h3 class="tw-strong">Mise en production</h3>
        {remaining && <span class="fb-muted tw-small tw-push">{remaining}</span>}
      </div>
      <p class="tw-small">
        Fusionner sur <code class="fb-mono">{defaultBranch}</code> déclenche le déploiement existant.
        Seul l'approbateur désigné peut fusionner : Bitbucket l'impose.
      </p>
      <ul class="tw-checks">
        {checks.map((c) => (
          <li key={c.key} data-testid={`tower-check-${c.key}`}>
            {c.ok ? <Icons.Check size={16} class="tw-ok" label="vert" /> : <Icons.X size={16} class="tw-ko" label="rouge" />}
            {c.label}
            {(c.detail || (c.key === 'sha' && headSha)) && <span class="fb-mono fb-muted tw-push">{c.detail || headSha?.slice(0, 7)}</span>}
          </li>
        ))}
      </ul>
      {previewUrl && (
        <a href={previewUrl} target="_blank" rel="noreferrer" class="tw-preview-link">
          <span><span>Essayer la preview</span><span class="fb-mono fb-muted">{previewUrl.replace(/^https:\/\//, '')}</span></span>
          <Icons.External size={16} />
        </a>
      )}
      {isApprover && note !== null && (
        <form class="tw-section" onSubmit={(e) => {
          e.preventDefault()
          onSendBack(note)
          setNote(null)
        }}>
          <label class="dash-field">Ce que l'agent doit reprendre
            <textarea class="fb-input" rows={3} value={note} autoFocus data-testid="tower-send-back-note"
              placeholder="Par exemple : le bouton doit rester visible sur mobile."
              onInput={(e) => setNote(e.currentTarget.value)} />
          </label>
          <div class={cx('tw-actions', compact && 'is-compact')}>
            <Button variant="ghost" onClick={() => setNote(null)} disabled={busy}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={busy} testId="tower-send-back-submit"><Icons.Reply size={14} />Renvoyer</Button>
          </div>
        </form>
      )}
      {isApprover && note === null && (
        <div class={cx('tw-actions', compact && 'is-compact')}>
          <Button onClick={() => setNote('')} disabled={busy} testId="tower-send-back-button">Renvoyer à l'agent</Button>
          <Button variant="primary" onClick={onApprove} disabled={busy || !canApprove} testId="tower-approve-button">
            {busy ? <Spinner size={14} /> : <Icons.Shield size={14} />}
            Approuver et ouvrir la PR
          </Button>
        </div>
      )}
      {!isApprover && <p class="fb-muted tw-small">Seuls les approbateurs désignés du projet peuvent approuver.</p>}
      {!canApprove && isApprover && note === null && <p class="fb-muted tw-small">Un contrôle n'est pas vert : l'approbation attend.</p>}
    </section>
  )
}
